import React, { useEffect } from 'react';
import { createNativeStackNavigator, NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNavigation } from '@react-navigation/native';
import { ActivityIndicator, View } from 'react-native';
import { COLORS } from '../constants/config';
import { useAuth } from '../contexts/AuthContext';
import { useRoleAccess } from '../hooks/useRoleAccess';
import AccessDeniedScreen from '../screens/auth/AccessDeniedScreen';

// =====================================================================
// Screen Imports — All Talents screens
// =====================================================================
import TalentsScreen from '../screens/talents/TalentsScreen';
import SpecializationDetailScreen from '../screens/talents/SpecializationDetailScreen';
import ProjectDetailScreen from '../screens/talents/ProjectDetailScreen';
import AddProjectScreen from '../screens/talents/AddProjectScreen';
import OutputGalleryScreen from '../screens/talents/OutputGalleryScreen';
import AddOutputScreen from '../screens/talents/AddOutputScreen';
import StudentDetailScreen from '../screens/talents/StudentDetailScreen';
import AddTalentActivityScreen from '../screens/talents/AddTalentActivityScreen';
import TalentActivityDetailScreen from '../screens/talents/TalentActivityDetailScreen';
import TeacherDetailScreen from '../screens/talents/TeacherDetailScreen';
import EditTeacherScreen from '../screens/talents/EditTeacherScreen';
import EditStudentScreen from '../screens/talents/EditStudentScreen';
import EditTalentsScreen from '../screens/talents/EditTalentsScreen';
import AddTalentKPIScreen from '../screens/talents/AddTalentKPIScreen';
import TalentKPIDetailScreen from '../screens/talents/TalentKPIDetailScreen';
import AddTalentKPIReadingScreen from '../screens/talents/AddTalentKPIReadingScreen';
import StudentTalentRegisterScreen from '../screens/talents/StudentTalentRegisterScreen';
import GuardianHomeScreen from '../screens/talents/GuardianHomeScreen';
import GuardianEditScreen from '../screens/talents/GuardianEditScreen';
import SettingsScreen from '../screens/settings/SettingsScreen';

// =====================================================================
// Route Param Types
// =====================================================================
export type MainStackParamList = {
  Talents: undefined;
  // studentId carries which child a guardian navigated from, so the detail
  // screens can show that child's own progress rather than the cohort's.
  SpecializationDetail: { id?: number; name: string; entityName?: string; fromEntities?: boolean; description?: string; studentId?: number };
  ProjectDetail: { name: string; id?: number; entityName?: string; studentId?: number };
  AddProject: { specializationId: number; projectId?: number };
  OutputGallery: { id?: number; name: string };
  AddOutput: { projectId: number };
  StudentDetail: { id: number; name: string; entityName?: string };
  AddTalentActivity: { projectId?: number; specializationId?: number; activityId?: number };
  TalentActivityDetail: { id: number; entityName?: string; studentId?: number };
  AddTalentKPI: { specializationId?: number; projectId?: number; activityId?: number; studentId?: number };
  TalentKPIDetail: { kpiLinkId: number };
  AddTalentKPIReading: { kpiLinkId: number };
  EditTalents: { mode?: 'info' | 'talents' | 'add' | 'schedule'; specId?: number };
  TeacherDetail: { id: number; entityName?: string };
  EditTeacher: { id?: number; specializationId?: number };
  EditStudent: { id?: number; specializationId?: number };
  StudentProfile: undefined;
  CoordinatorProfile: undefined;
  StudentTalentRegister: { studentId?: number } | undefined;
  Guardian: undefined;
  GuardianEdit: { mode: 'self' | 'child'; studentId?: number };
  Settings: undefined;
};

const Stack = createNativeStackNavigator<MainStackParamList>();

const stackScreenOptions = {
  headerShown: false,
  animation: 'none' as const,
};

// =====================================================================
// Guardian route guard
// =====================================================================
/**
 * A guardian manages their child's account and registers talents. Every staff
 * screen below is refused by the API anyway (`IsReadOnlyOrScoped` gives the role
 * SAFE_METHODS only), but the routes stayed reachable by typing a URL, which
 * rendered a form that could only ever fail on save. Send them home instead.
 */
function blockForGuardian<P extends object>(Component: React.ComponentType<P>) {
  return function GuardianGuarded(props: P) {
    const { isGuardian } = useRoleAccess();
    const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();

    useEffect(() => {
      if (isGuardian) {
        navigation.reset({ index: 0, routes: [{ name: 'Guardian' }] });
      }
    }, [isGuardian, navigation]);

    if (isGuardian) return null;
    return <Component {...props} />;
  };
}

// Wrapped once at module scope — wrapping inside render would remount the
// screen on every navigator re-render and lose its state.
const GuardedAddProject = blockForGuardian(AddProjectScreen);
const GuardedAddOutput = blockForGuardian(AddOutputScreen);
const GuardedAddTalentActivity = blockForGuardian(AddTalentActivityScreen);
const GuardedAddTalentKPI = blockForGuardian(AddTalentKPIScreen);
const GuardedAddTalentKPIReading = blockForGuardian(AddTalentKPIReadingScreen);
const GuardedEditStudent = blockForGuardian(EditStudentScreen);
const GuardedEditTeacher = blockForGuardian(EditTeacherScreen);
const GuardedEditTalents = blockForGuardian(EditTalentsScreen);
const GuardedTeacherDetail = blockForGuardian(TeacherDetailScreen);
const GuardedCoordinatorProfile = blockForGuardian(TeacherDetailScreen);
const GuardedStudentProfile = blockForGuardian(StudentDetailScreen);

// =====================================================================
// Talents Stack — All screens
// =====================================================================
function TalentsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Talents" component={TalentsScreen} />
      <Stack.Screen name="SpecializationDetail" component={SpecializationDetailScreen} />
      <Stack.Screen name="ProjectDetail" component={ProjectDetailScreen} />
      <Stack.Screen name="AddProject" component={GuardedAddProject} />
      <Stack.Screen name="OutputGallery" component={OutputGalleryScreen} />
      <Stack.Screen name="AddOutput" component={GuardedAddOutput} />
      <Stack.Screen name="StudentDetail" component={StudentDetailScreen} />
      <Stack.Screen name="AddTalentActivity" component={GuardedAddTalentActivity} />
      <Stack.Screen name="TalentActivityDetail" component={TalentActivityDetailScreen} />
      <Stack.Screen name="TeacherDetail" component={GuardedTeacherDetail} />
      <Stack.Screen name="EditTeacher" component={GuardedEditTeacher} />
      <Stack.Screen name="EditStudent" component={GuardedEditStudent} />
      <Stack.Screen name="EditTalents" component={GuardedEditTalents} />
      <Stack.Screen name="AddTalentKPI" component={GuardedAddTalentKPI} />
      <Stack.Screen name="TalentKPIDetail" component={TalentKPIDetailScreen} />
      <Stack.Screen name="AddTalentKPIReading" component={GuardedAddTalentKPIReading} />
      <Stack.Screen name="StudentProfile" component={GuardedStudentProfile} />
      <Stack.Screen name="CoordinatorProfile" component={GuardedCoordinatorProfile} />
      <Stack.Screen name="StudentTalentRegister" component={StudentTalentRegisterScreen} />
      <Stack.Screen name="Guardian" component={GuardianHomeScreen} />
      <Stack.Screen name="GuardianEdit" component={GuardianEditScreen} />
      <Stack.Screen name="Settings" component={SettingsScreen} />
    </Stack.Navigator>
  );
}

// =====================================================================
// Main Navigator — top-navbar layout, no drawer
// =====================================================================
export default function MainNavigator() {
  const { isAuthenticated, isLoading, accessDenial } = useAuth();

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  if (accessDenial) {
    return <AccessDeniedScreen />;
  }

  if (!isAuthenticated) {
    // AuthContext handles SSO redirect — just show spinner while it processes
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  return <TalentsStack />;
}
